import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { isAxiosError } from "axios";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { slugify } from "@endeks/shared";
import { useSocialProject, useCreateSocialProject, useUpdateSocialProject } from "./queries";
import { EntityTabs, type EntityTab } from "../../components/Editor/EntityTabs";
import { LangToggle, type Lang } from "../../components/Editor/LangToggle";
import { RichTextEditor } from "../../components/Content/RichTextEditor";
import SocialImagesField from "./SocialImagesField";
import { useUnsavedChanges } from "../../hooks/useUnsavedChanges";
import { checkRequired, isBlank, isBlankHtml } from "@/lib/form-validation";
import { errorMessage } from "@/lib/error-message";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { EN_ENTITY_BASES } from "@/config/site-routes";

export default function SocialForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const { data: project, isLoading } = useSocialProject(id ?? "");
  const createMutation = useCreateSocialProject();
  const updateMutation = useUpdateSocialProject();

  const [lang, setLang] = useState<Lang>("tr");
  const [tab, setTab] = useState<EntityTab>("content");

  // Form state
  const [title, setTitle] = useState("");
  const [titleEn, setTitleEn] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEn, setSlugEn] = useState("");
  // EN slug elle düzenlenmediyse EN başlıktan otomatik önerilir
  // Yeni kayıtta slug, kullanıcı elle düzenleyene kadar başlığı takip eder.
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugEnTouched, setSlugEnTouched] = useState(false);
  const [excerpt, setExcerpt] = useState("");
  const [excerptEn, setExcerptEn] = useState("");
  const [content, setContent] = useState("");
  const [contentEn, setContentEn] = useState("");
  const [images, setImages] = useState<string[]>([]);
  const [order, setOrder] = useState(0);
  const [published, setPublished] = useState(false);

  // Kaydedilmemiş değişiklik takibi: yüklenen ilk değerlerle karşılaştırılır.
  const [loaded, setLoaded] = useState(!isEdit);
  const [baseline, setBaseline] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const snapshot = JSON.stringify({
    title,
    titleEn,
    slug,
    slugEn,
    excerpt,
    excerptEn,
    // Boş editör "" ya da "<p></p>" olabilir; ikisi de aynı sayılır
    content: isBlankHtml(content) ? "" : content,
    contentEn: isBlankHtml(contentEn) ? "" : contentEn,
    images,
    order,
    published,
  });
  // İlk değerler yüklendiği render'da taban alınır (effect yerine render sırasında)
  if (loaded && baseline === null) setBaseline(snapshot);

  // Populate on edit
  useEffect(() => {
    if (project) {
      setTitle(project.title ?? "");
      setTitleEn(project.titleEn ?? "");
      setSlug(project.slug ?? "");
      // EN slug boşsa EN başlıktan önerilir. Eski EN adresi (TR slug) web'de yeni
      // adrese yönlendiği için link kırılmaz; dolu bir EN slug ise asla değiştirilmez.
      setSlugEn(project.slugEn || (project.titleEn ? slugify(project.titleEn) : ""));
      setSlugEnTouched(!!project.slugEn);
      setExcerpt(project.excerpt ?? "");
      setExcerptEn(project.excerptEn ?? "");
      setContent(project.content ?? "");
      setContentEn(project.contentEn ?? "");
      setImages(project.images ?? []);
      setOrder(project.order ?? 0);
      setPublished(project.published ?? false);
      setLoaded(true);
      setBaseline(null);
    }
  }, [project]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const valid = checkRequired<EntityTab>(
      [
        {
          label: "Başlık",
          missing: isBlank(title),
          target: { tab: "content", lang: "tr", id: "title" },
        },
        {
          label: "Slug",
          missing: isBlank(slug),
          target: { tab: "content", lang: "tr", id: "slug" },
        },
        {
          label: "Özet",
          missing: isBlank(excerpt),
          target: { tab: "content", lang: "tr", id: "excerpt" },
        },
        {
          label: "İçerik",
          missing: isBlankHtml(content),
          target: { tab: "content", lang: "tr", id: "content" },
        },
      ],
      (t) => {
        if (t.tab) setTab(t.tab);
        if (t.lang) setLang(t.lang);
      },
    );
    if (!valid) return;
    try {
      const payload = {
        title,
        titleEn: titleEn || null,
        slug,
        slugEn: slugEn || null,
        excerpt,
        excerptEn: excerptEn || null,
        content,
        contentEn: contentEn || null,
        images,
        order,
        published,
      };

      if (isEdit) {
        await updateMutation.mutateAsync({ id: id!, ...payload });
        toast.success("Proje güncellendi");
      } else {
        await createMutation.mutateAsync(payload);
        toast.success("Proje oluşturuldu");
      }
      setSaved(true);
      navigate("/social");
    } catch (error: unknown) {
      toast.error(isAxiosError(error) ? errorMessage(error, "Bir hata oluştu") : "Bir hata oluştu");
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending;
  const dirty = baseline !== null && snapshot !== baseline && !saved;
  const { confirmNavigate, dialog: unsavedDialog } = useUnsavedChanges(dirty);
  const seoPath = `/hakkimizda/sosyal-sorumluluk/${slug || ""}`;
  // SEO kaydı TR yolunda tutulur; önizleme URL'i seçili dilin yolunu ve slug'ını
  // gösterir (EN: İngilizce segment + EN slug, yoksa TR slug; "/en" öneki GooglePreview'da)
  const previewPath = lang === "en" ? `${EN_ENTITY_BASES.social}/${slugEn || slug || ""}` : seoPath;

  if (isEdit && isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const contentPanel = (
    <form id="social-form" noValidate onSubmit={handleSubmit} className="space-y-5 pt-4">
      {/* Lang toggle */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground">Dil:</span>
        <LangToggle value={lang} onChange={setLang} />
      </div>

      {/* title */}
      {lang === "tr" ? (
        <div className="space-y-1.5">
          <Label htmlFor="title">
            Başlık <span className="text-destructive">*</span>
          </Label>
          <Input
            id="title"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (!isEdit && !slugTouched) setSlug(slugify(e.target.value));
            }}
            required
          />
        </div>
      ) : (
        <div className="space-y-1.5">
          <Label htmlFor="titleEn">Başlık (English)</Label>
          <Input
            id="titleEn"
            value={titleEn}
            onChange={(e) => {
              setTitleEn(e.target.value);
              if (!slugEnTouched) setSlugEn(slugify(e.target.value));
            }}
          />
        </div>
      )}

      {/* slug */}
      {lang === "tr" ? (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="slug">
              Slug <span className="text-destructive">*</span>
            </Label>
            <button
              type="button"
              className="text-xs text-primary hover:underline disabled:opacity-50"
              disabled={!title.trim()}
              onClick={() => {
                setSlug(slugify(title));
                setSlugTouched(true);
              }}
              title={isEdit ? "Dikkat: yayındaki yazının adresi değişir" : undefined}
            >
              Başlıktan oluştur
            </button>
          </div>
          <Input
            id="slug"
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value);
              setSlugTouched(true);
            }}
            // Kullanıcı slug'ı elle değiştirdiyse (ya da yeni kayıtsa) biçimi düzelt;
            // dokunulmamış mevcut slug'a dokunma (canlı URL değişmesin).
            onBlur={() => (!isEdit || slugTouched) && setSlug(slugify(slug))}
            placeholder="proje-adi"
            required
          />
        </div>
      ) : (
        <div className="space-y-1.5">
          <Label htmlFor="slugEn">Slug (EN)</Label>
          <Input
            id="slugEn"
            value={slugEn}
            onChange={(e) => {
              setSlugEn(e.target.value);
              setSlugEnTouched(true);
            }}
            onBlur={() => setSlugEn(slugify(slugEn))}
            placeholder={slug || "project-name"}
          />
          <p className="text-xs text-muted-foreground">
            Boş bırakılırsa TR slug kullanılır{slug ? ` (${slug})` : ""}.
          </p>
        </div>
      )}

      {/* excerpt */}
      {lang === "tr" ? (
        <div className="space-y-1.5">
          <Label htmlFor="excerpt">
            Özet <span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="excerpt"
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            className="min-h-[80px]"
          />
        </div>
      ) : (
        <div className="space-y-1.5">
          <Label htmlFor="excerptEn">Özet (English)</Label>
          <Textarea
            id="excerptEn"
            value={excerptEn}
            onChange={(e) => setExcerptEn(e.target.value)}
            className="min-h-[80px]"
          />
        </div>
      )}

      {/* content */}
      {lang === "tr" ? (
        <div className="space-y-1.5">
          <Label>
            İçerik <span className="text-destructive">*</span>
          </Label>
          <RichTextEditor id="content" value={content} onChange={setContent} />
        </div>
      ) : (
        <div className="space-y-1.5">
          <Label>İçerik (English)</Label>
          <RichTextEditor value={contentEn} onChange={setContentEn} />
        </div>
      )}

      {/* order (always visible) */}
      <div className="space-y-1.5">
        <Label htmlFor="order">Sıra</Label>
        <Input
          id="order"
          type="number"
          min={0}
          value={order}
          onChange={(e) => setOrder(Number(e.target.value))}
          className="w-32"
        />
      </div>

      {/* published (always visible) */}
      <div className="flex items-center gap-3">
        <Switch id="published" checked={published} onCheckedChange={setPublished} />
        <Label htmlFor="published">Yayında</Label>
      </div>
    </form>
  );

  const mediaPanel = (
    <div className="space-y-4 pt-4">
      <SocialImagesField value={images} onChange={setImages} />
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => confirmNavigate("/social")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-xl font-semibold">{isEdit ? "Projeyi Düzenle" : "Yeni Proje"}</h1>
      </div>

      <EntityTabs
        value={tab}
        onValueChange={setTab}
        seoDisabled={!isEdit}
        locale={lang}
        seoPath={seoPath}
        previewPath={previewPath}
        content={contentPanel}
        media={mediaPanel}
      />

      {/* Submit bar */}
      <div className="flex gap-2 pt-2">
        <Button form="social-form" type="submit" disabled={isPending}>
          {isPending ? "Kaydediliyor…" : isEdit ? "Güncelle" : "Oluştur"}
        </Button>
        <Button variant="outline" onClick={() => confirmNavigate("/social")}>
          İptal
        </Button>
      </div>
      {unsavedDialog}
    </div>
  );
}
